import React, { useMemo } from "react";
import classNames from "classnames";
import copy from "copy-to-clipboard";
import { COUPON_STATUS, PRIZE_COUPON_TYPES } from "../../../../constants";
import { IPrizeCoupon } from "../../../../types";
import { sendMessageToParent } from "../../../../utils";
import {
  CouponAvailability,
  getFreeShippingAvailability,
  getFreeShippingMonth,
} from "../MyCoupons/utils";
import styles from "./index.module.scss";

interface MyCouponItemProps {
  coupon: IPrizeCoupon;
  currentMonth: number;
  currentYear: number;
}

const monthFormatter = new Intl.DateTimeFormat("en-US", { month: "long" });

const getMonthName = (month: number): string =>
  monthFormatter.format(new Date(Date.UTC(2020, month - 1, 1)));

const formatMoney = (value?: number): string => {
  if (value === undefined) {
    return "$0";
  }

  return `$${Number.isInteger(value) ? value : value.toFixed(2)}`;
};

const getAvailabilityLabel = (availability: CouponAvailability): string => {
  if (availability === "not-issued") {
    return "Not issued";
  }

  if (availability === "expired") {
    return "Expired";
  }

  if (availability === "used") {
    return "Used";
  }

  return "Copy";
};

const MyCouponItem: React.FC<MyCouponItemProps> = ({
  coupon,
  currentMonth,
  currentYear,
}) => {
  const isFreeShipping =
    coupon.discount_code_type === PRIZE_COUPON_TYPES.FREE_SHIPPING;
  const couponMonth = getFreeShippingMonth(coupon.free_shipping_type);
  const monthName = couponMonth ? getMonthName(couponMonth) : "";
  const availability = useMemo<CouponAvailability>(() => {
    if (coupon.status !== COUPON_STATUS.AVAILABLE) {
      return "used";
    }

    if (!isFreeShipping) {
      return "copy";
    }

    return getFreeShippingAvailability(coupon.free_shipping_type, currentMonth);
  }, [
    coupon.free_shipping_type,
    coupon.status,
    currentMonth,
    isFreeShipping,
  ]);
  const isCopyable = availability === "copy";

  const handleCopy = () => {
    if (availability === "used") {
      sendMessageToParent({
        type: "member-toast",
        data: {
          type: "error",
          message: "This coupon has already been used.",
          duration: 3000,
        },
      });
      return;
    }

    if (!isCopyable || !coupon.shopify_discount_code) {
      return;
    }

    const isCopied = copy(coupon.shopify_discount_code, {
      debug: false,
      format: "text/plain",
    });

    if (isCopied) {
      sendMessageToParent({
        type: "member-toast",
        data: {
          type: "success",
          message: "Copied Successfully!",
          duration: 3000,
        },
      });
    }
  };

  const title = useMemo(() => {
    if (isFreeShipping) {
      return "FREE SHIPPING";
    }

    if (coupon.discount_code_type === PRIZE_COUPON_TYPES.LOTTERY_AMOUNT_OFF) {
      return `${formatMoney(coupon.n)} OFF`;
    }

    return `GET ${coupon.n} FREE`;
  }, [coupon.discount_code_type, coupon.n, isFreeShipping]);

  const description = useMemo(() => {
    if (isFreeShipping) {
      return `${monthName} Monthly Standard Shipping Coupon`;
    }

    if (coupon.discount_code_type === PRIZE_COUPON_TYPES.LOTTERY_AMOUNT_OFF) {
      return `when you spend ${formatMoney(coupon.m)}`;
    }

    if (typeof coupon.m !== "number" || typeof coupon.n !== "number") {
      return "";
    }

    return `Buy ${coupon.m + coupon.n} Get ${coupon.n} Free`;
  }, [
    coupon.discount_code_type,
    coupon.m,
    coupon.n,
    isFreeShipping,
    monthName,
  ]);

  return (
    <article className={styles.couponItem}>
      <div className={styles.couponLabel}>
        <span>{isFreeShipping ? "Member Reward" : "Mystery Box Rewards"}</span>
      </div>

      <div className={styles.couponContent}>
        <div className={styles.leftSection}>
          {isFreeShipping && (
            <div className={styles.eyebrow}>
              {monthName.toUpperCase()} {currentYear}
            </div>
          )}
          <div className={styles.title}>{title}</div>
          <div className={styles.description}>{description}</div>
          <div className={styles.meta}>
            {isFreeShipping
              ? `Valid in ${monthName}`
              : "BFCM Early Access Reward"}
          </div>
          {!isFreeShipping && (
            <div className={styles.meta}>Valid through Dec 31</div>
          )}
        </div>

        <div className={styles.rightSection}>
          <button
            type="button"
            data-element-id={isCopyable ? "copy-my-coupon-code" : undefined}
            className={classNames(styles.actionButton, {
              [styles.disabled]: !isCopyable,
            })}
            disabled={!isCopyable && availability !== "used"}
            onClick={handleCopy}
          >
            {getAvailabilityLabel(availability)}
          </button>
        </div>
      </div>
    </article>
  );
};

export default MyCouponItem;
